import { create } from "zustand";
import type { GameState } from "@/types/game";
import type { Hero } from "@/types/heroes";
import type { DeckCard } from "@/types/cards";
import type { LoadedData } from "@/data/loader";
import { apply, setCatalog, type EngineAction } from "@/engine/GameEngine";
import { makeInitialState } from "@/engine/factories";

export type TargetMode =
  | "none"
  | "attack"
  | "spell"
  | "supply"
  | "effect"
  | "pair"
  | "slot"
  | "suit"
  | "steal-card"
  | "discard";

interface GameStore {
  data: LoadedData | null;
  state: GameState | null;
  appPhase: "select" | "playing";
  humanPlayerId: string;
  selectedCardUid: number | null;
  targetMode: TargetMode;
  pendingSkillName: string | null;
  pendingPairFirst: string | null;
  pendingSlotTargetId: string | null; // 缴械/掠夺：已选定目标，待选装备槽
  pendingSuitTargetId: string | null; // 杀戮之心：已选定目标，待选花色
  pendingStealTargetId: string | null; // 飞龙探云手：已选定目标，待背面盲选手牌
  pendingDiscardUids: number[];
  rescueDismissed: boolean;
  substitutePicks: number[]; // 替换出牌选中的2张手牌 uid
  autoSkipEmptyResponse: boolean; // 自动跳过无可响应牌的响应窗口（会泄露"无牌"信息）

  load: (data: LoadedData) => void;
  newGame: (opts: {
    heroes: [Hero, Hero, Hero, Hero];
    humanSeat: 0 | 1 | 2 | 3;
    deck: DeckCard[];
    seed: number;
  }) => void;
  backToSelect: () => void;
  dispatch: (action: EngineAction) => void;
  setSelectedCard: (uid: number | null) => void;
  setTargetMode: (m: TargetMode) => void;
  setPendingSkillName: (name: string | null) => void;
  setPendingPairFirst: (id: string | null) => void;
  setPendingSlotTargetId: (id: string | null) => void;
  setPendingSuitTargetId: (id: string | null) => void;
  setPendingStealTargetId: (id: string | null) => void;
  toggleDiscardUid: (uid: number) => void;
  clearDiscardUids: () => void;
  setRescueDismissed: (v: boolean) => void;
  setSubstitutePicks: (uids: number[]) => void;
  clearSubstitutePicks: () => void;
  setAutoSkipEmptyResponse: (v: boolean) => void;
}

export const useGameStore = create<GameStore>((set) => ({
  data: null,
  state: null,
  appPhase: "select",
  humanPlayerId: "p0",
  selectedCardUid: null,
  targetMode: "none",
  pendingSkillName: null,
  pendingPairFirst: null,
  pendingSlotTargetId: null,
  pendingSuitTargetId: null,
  pendingStealTargetId: null,
  pendingDiscardUids: [],
  rescueDismissed: false,
  substitutePicks: [],
  autoSkipEmptyResponse: false,

  load: (data) => {
    setCatalog(data.catalog);
    set({ data });
  },

  newGame: ({ heroes, humanSeat, deck, seed }) => {
    const init = makeInitialState(heroes, deck, seed, humanSeat);
    set({
      state: apply(init, { type: "start-game" }),
      appPhase: "playing",
      humanPlayerId: "p" + humanSeat,
      selectedCardUid: null,
      targetMode: "none",
      pendingSkillName: null,
      pendingPairFirst: null,
      pendingSlotTargetId: null,
      pendingSuitTargetId: null,
      pendingStealTargetId: null,
      pendingDiscardUids: [],
      rescueDismissed: false,
    });
  },

  backToSelect: () =>
    set({
      state: null,
      appPhase: "select",
      selectedCardUid: null,
      targetMode: "none",
      pendingSkillName: null,
      pendingPairFirst: null,
      pendingSlotTargetId: null,
      pendingSuitTargetId: null,
      pendingStealTargetId: null,
      pendingDiscardUids: [],
      rescueDismissed: false,
    }),

  dispatch: (action) =>
    set((s) => {
      if (!s.state) return {};
      const next = apply(s.state, action);
      // 弃牌选择仅服务于当前弃牌阶段：成功弃牌/结束回合/开启新回合后必须清空，
      // 否则残留的旧 uid 会让下一次弃牌阶段"未选就已达数量"（旧 uid 不在手牌中，
      // 点击确认实际弃 0 张，真选牌后反而因数量不符被禁用）
      const clearDiscard =
        action.type === "discard" ||
        action.type === "end-turn" ||
        action.type === "begin-turn";
      return {
        state: next,
        selectedCardUid: null,
        targetMode: "none",
        pendingSkillName: null,
        pendingPairFirst: null,
        pendingSlotTargetId: null,
        pendingSuitTargetId: null,
        pendingStealTargetId: null,
        ...(clearDiscard ? { pendingDiscardUids: [] } : {}),
      };
    }),

  setSelectedCard: (uid) => set({ selectedCardUid: uid }),
  setTargetMode: (m) => set({ targetMode: m }),
  setPendingSkillName: (name) => set({ pendingSkillName: name }),
  setPendingPairFirst: (id) => set({ pendingPairFirst: id }),
  setPendingSlotTargetId: (id) => set({ pendingSlotTargetId: id }),
  setPendingSuitTargetId: (id) => set({ pendingSuitTargetId: id }),
  setPendingStealTargetId: (id) => set({ pendingStealTargetId: id }),
  toggleDiscardUid: (uid) =>
    set((s) => {
      const has = s.pendingDiscardUids.includes(uid);
      return {
        pendingDiscardUids: has
          ? s.pendingDiscardUids.filter((x) => x !== uid)
          : [...s.pendingDiscardUids, uid],
      };
    }),
  clearDiscardUids: () => set({ pendingDiscardUids: [] }),
  setRescueDismissed: (v) => set({ rescueDismissed: v }),
  setSubstitutePicks: (uids: number[]) => set({ substitutePicks: uids }),
  clearSubstitutePicks: () => set({ substitutePicks: [] }),
  setAutoSkipEmptyResponse: (v) => set({ autoSkipEmptyResponse: v }),
}));

// 调试：浏览器环境暴露 store 到 window
if (typeof window !== "undefined") {
  (window as unknown as { __gameStore?: unknown }).__gameStore = useGameStore;
}
